import { X, Tags, Pencil, CheckCircle, XCircle, FileText, Calendar } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import type { AssetCategory } from '../../../types/asset';

interface AssetCategoryDetailsModalProps {
  open: boolean;
  onClose: () => void;
  category: AssetCategory | null;
  onEdit: (category: AssetCategory) => void;
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AssetCategoryDetailsModal({ open, onClose, category, onEdit }: AssetCategoryDetailsModalProps) {
  if (!open || !category) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-border bg-background/50 rounded-t-2xl">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Tags size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-text truncate">{category.name}</h2>
              <p className="text-xs text-text-muted">Category details</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-background rounded-xl transition-colors"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-text-muted uppercase mb-1">Code</p>
              <p className="text-sm font-mono text-text">{category.code || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-text-muted uppercase mb-1">Status</p>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  category.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                }`}
              >
                {category.isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                {category.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-text-muted uppercase mb-1">Description</p>
            <div className="flex items-start gap-2 text-sm text-text">
              <FileText size={15} className="text-text-muted shrink-0 mt-0.5" />
              <span className="whitespace-pre-wrap">{category.description || 'No description provided.'}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-border">
            <div>
              <p className="text-xs font-medium text-text-muted uppercase mb-1">Created</p>
              <p className="text-sm text-text flex items-center gap-2">
                <Calendar size={14} className="text-text-muted" />
                {formatDate(category.createdAt)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-text-muted uppercase mb-1">Last updated</p>
              <p className="text-sm text-text flex items-center gap-2">
                <Calendar size={14} className="text-text-muted" />
                {formatDate(category.updatedAt)}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button type="button" onClick={() => onEdit(category)}>
            <Pencil size={16} className="mr-2" />
            Edit
          </Button>
        </div>
      </div>
    </div>
  );
}
